/*
 * Eximely – small progressive enhancements layered on Minimog.
 * - Blocks duplicate add-to-cart submissions while a request is in flight.
 * - Keeps preorder button labels and blocked preorders correct after variant changes.
 * - Updates the availability line for the selected variant.
 * - Announces cart updates and errors to assistive technology.
 * Everything here is optional: without it, Shopify's native forms still work.
 */
(() => {
  if (window.PegasusTheme) return;
  window.PegasusTheme = true;

  const strings = () => window.MinimogStrings || {};

  /* ---------- Live region ---------- */
  let liveRegion;
  const announce = (message, assertive = false) => {
    if (!message) return;
    if (!liveRegion) {
      liveRegion = document.createElement("div");
      liveRegion.className = "m:sr-only";
      liveRegion.setAttribute("role", "status");
      liveRegion.setAttribute("aria-live", "polite");
      document.body.appendChild(liveRegion);
    }
    liveRegion.setAttribute("aria-live", assertive ? "assertive" : "polite");
    liveRegion.textContent = "";
    window.setTimeout(() => {
      liveRegion.textContent = message;
    }, 50);
  };

  /* ---------- Duplicate submission guard ---------- */
  const BUSY_TIMEOUT = 8000;
  const release = (form) => {
    if (!form) return;
    delete form.dataset.pcBusy;
    window.clearTimeout(form._pcTimer);
  };
  const releaseAll = () => document.querySelectorAll("form[data-pc-busy]").forEach(release);

  document.addEventListener(
    "submit",
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (!form.matches('form[action*="/cart/add"]')) return;

      const wrapper = form.closest("[data-pc-buy-buttons]");
      if (wrapper && wrapper.dataset.pcBlocked === "true") {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      if (form.dataset.pcBusy === "true") {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      form.dataset.pcBusy = "true";
      form._pcTimer = window.setTimeout(() => release(form), BUSY_TIMEOUT);
    },
    true
  );

  document.addEventListener("product-ajax:added", () => {
    releaseAll();
    announce(strings().itemAdded);
  });
  document.addEventListener("product-ajax:error", (event) => {
    releaseAll();
    const message = event.detail && event.detail.errorMessage;
    announce(typeof message === "string" ? message : strings().cartError, true);
  });

  const subscribe = () => {
    const events = window.MinimogEvents;
    const theme = window.MinimogTheme;
    if (!events || !theme || !theme.pubSubEvents) return;
    events.subscribe(theme.pubSubEvents.cartError, (detail) => {
      releaseAll();
      if (detail && detail.source !== "product-form") {
        announce((detail && detail.message) || strings().cartError, true);
      }
    });
  };

  /* ---------- Preorder / blocked buttons ---------- */
  const applyBuyButtonState = (wrapper) => {
    const button = wrapper.querySelector('[name="add"]');
    if (!button) return;
    const label = wrapper.dataset.pcAtcLabel;
    const text = button.querySelector(".m-add-to-cart--text");
    if (wrapper.dataset.pcBlocked === "true") {
      if (!button.disabled) button.disabled = true;
      if (!button.classList.contains("disabled")) button.classList.add("disabled");
      if (button.getAttribute("aria-disabled") !== "true") button.setAttribute("aria-disabled", "true");
      if (text && label && text.textContent.trim() !== label) text.textContent = label;
      return;
    }
    if (!button.disabled && text && label && text.textContent.trim() !== label) text.textContent = label;
  };

  /* ---------- Variant availability line ---------- */
  const updateStatus = (variant) => {
    if (!variant) return;
    document.querySelectorAll("[data-pc-status-wrapper]").forEach((wrapper) => {
      const status = wrapper.querySelector("[data-pc-status]");
      const dataEl = wrapper.querySelector("[data-pc-status-data]");
      if (!status || !dataEl || status.classList.contains("pc-status--preorder")) return;
      let data;
      try {
        data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      const key = data.variants && data.variants[String(variant.id)];
      if (!key || !data.labels[key]) return;
      status.className = `pc-status pc-status--${key}`;
      const label = status.querySelector(".pc-status__text");
      if (label) label.textContent = data.labels[key];
    });
  };

  document.addEventListener("variant:changed", (event) => {
    const variant = event.detail && event.detail.variant;
    document.querySelectorAll("[data-pc-buy-buttons]").forEach(applyBuyButtonState);
    updateStatus(variant);
  });


  /* ---------- Mobile menu drawer: keyboard and screen reader support ---------- */
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';
  const initMenuDrawer = () => {
    const toggle = document.querySelector("[data-pc-menu-toggle]");
    const drawer = document.getElementById("m-menu-drawer");
    if (!toggle || !drawer || toggle.dataset.pcReady) return;
    toggle.dataset.pcReady = "true";
    const hamburger = toggle.querySelector(".m-hamburger-box");
    const isOpen = () => drawer.classList.contains("open");

    toggle.addEventListener("click", (event) => {
      // Keyboard activation targets the button itself; forward to Minimog's handler.
      if (hamburger && !hamburger.contains(event.target)) hamburger.click();
    });

    const sync = () => {
      const open = isOpen();
      toggle.setAttribute("aria-expanded", String(open));
      if (open) {
        drawer.removeAttribute("inert");
        const first = drawer.querySelector(FOCUSABLE);
        window.setTimeout(() => first && first.focus(), 50);
      } else {
        drawer.setAttribute("inert", "");
        if (drawer.contains(document.activeElement)) toggle.focus();
      }
    };
    new MutationObserver(sync).observe(drawer, { attributes: true, attributeFilter: ["class"] });
    if (window.matchMedia("(max-width: 1279px)").matches && !isOpen()) drawer.setAttribute("inert", "");

    document.addEventListener("keydown", (event) => {
      if (!isOpen()) return;
      if (event.key === "Escape") {
        event.preventDefault();
        hamburger && hamburger.click();
        toggle.focus();
        return;
      }
      if (event.key !== "Tab") return;
      const items = [...drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      const all = [toggle, ...items];
      const first = all[0];
      const last = all[all.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };


  /* ---------- Reveal on scroll ---------- */
  const initReveal = (root = document) => {
    const items = root.querySelectorAll(".pc-reveal:not(.is-visible)");
    if (!items.length) return;
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      items.forEach((el) => el.classList.add("is-visible"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.1 }
    );
    items.forEach((el) => io.observe(el));
  };

  /* Minimog's variant picker loads later and rewrites the button – keep preorder state authoritative */
  const guardBuyButtons = (root = document) => {
    root.querySelectorAll("[data-pc-buy-buttons]").forEach((wrapper) => {
      if (wrapper._pcGuard) return;
      const button = wrapper.querySelector('[name="add"]');
      if (!button) return;
      let applying = false;
      wrapper._pcGuard = new MutationObserver(() => {
        if (applying) return;
        applying = true;
        applyBuyButtonState(wrapper);
        window.requestAnimationFrame(() => (applying = false));
      });
      wrapper._pcGuard.observe(button, { attributes: true, attributeFilter: ["disabled", "class"], childList: true, subtree: true, characterData: true });
      applyBuyButtonState(wrapper);
    });
  };


  /* ---------- Vault hero pointer parallax (desktop, motion-safe) ---------- */
  const initParallax = (root = document) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (!window.matchMedia("(hover: hover) and (min-width: 990px)").matches) return;
    root.querySelectorAll("[data-pc-parallax]").forEach((hero) => {
      if (hero._pcParallax) return;
      hero._pcParallax = true;
      const layers = [...hero.querySelectorAll(".pc-vh__layer")];
      let raf = 0;
      hero.addEventListener("pointermove", (e) => {
        const r = hero.getBoundingClientRect();
        const dx = (e.clientX - r.left) / r.width - 0.5;
        const dy = (e.clientY - r.top) / r.height - 0.5;
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          layers.forEach((l) => {
            const d = parseFloat(getComputedStyle(l).getPropertyValue("--depth")) || 1;
            l.style.setProperty("--px", `${(-dx * d * 6).toFixed(1)}px`);
            l.style.setProperty("--py", `${(-dy * d * 6).toFixed(1)}px`);
          });
        });
      });
      hero.addEventListener("pointerleave", () => layers.forEach((l) => { l.style.setProperty("--px", "0px"); l.style.setProperty("--py", "0px"); }));
    });
  };

  const init = () => {
    subscribe();
    initParallax();
    guardBuyButtons();
    initReveal();
    initMenuDrawer();
    document.querySelectorAll("[data-pc-buy-buttons]").forEach(applyBuyButtonState);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  window.addEventListener("pageshow", releaseAll);

  /* Theme editor: re-apply when sections are re-rendered */
  document.addEventListener("shopify:section:load", (event) => {
    event.target.querySelectorAll("[data-pc-buy-buttons]").forEach(applyBuyButtonState);
    initMenuDrawer();
    initReveal(event.target);
    guardBuyButtons(event.target);
    initParallax(event.target);
  });
})();
